import { useId } from 'react';
import { CATEGORY_THRESHOLDS } from '@/domain/scoring';
import type { ResultCategory } from '@/domain/types';
import styles from './AlignmentDiagram.module.css';

/**
 * ===========================================================================
 * THE OVERLAP PICTURE
 * ===========================================================================
 *
 * Three shapes, one per ending, and each one moves a little with the score.
 *
 *  - `strong-potential`   two ovals leaning together, meeting in a heart
 *  - `worth-a-conversation`  two circles with a generous shared middle
 *  - `different-paths`    two circles that only graze each other
 *
 * The score moves the shapes *within their own band* rather than across the
 * whole 0–1 range. So a result that scraped into a category looks like it
 * scraped in, and one at the top of its band looks like it, without the
 * picture ever contradicting the words above it.
 *
 * This is the only "how much" on the page. It stays a picture on purpose: the
 * model is a weighted average of hand-authored judgements, which is enough to
 * say "quite a lot" honestly and nowhere near enough to say "82%".
 */

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

/** Where `score` sits inside a band, 0 at the bottom of it and 1 at the top. */
const within = (score: number, from: number, to: number) =>
  clamp01((score - from) / (to - from));

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/* A tall-ish box: wide enough for two circles side by side, tall enough for a
   heart, and the same for all three so the page does not jump between them. */
const VIEW = { width: 300, height: 180, midX: 150 };

const LABEL: Record<ResultCategory, string> = {
  'strong-potential':
    'Two ovals leaning together, overlapping in the shape of a heart',
  'worth-a-conversation': 'Two circles with a wide shared area between them',
  'different-paths': 'Two circles that only just overlap',
};

export interface AlignmentDiagramProps {
  category: ResultCategory;
  /** The weighted score, 0–1. Moves the shapes inside their own band. */
  score: number;
  /**
   * A gated result. The score can be high and the ending still be
   * `different-paths`, and in that case the picture has to agree with the
   * words rather than with the arithmetic.
   */
  hasConflict?: boolean;
}

/* ---------------------------------------------------------------------------
   Two circles — the middle and the far ending
   --------------------------------------------------------------------------- */

const CIRCLE_R = 58;

/**
 * Distance between the two centres. `2 × r` (116) is the moment they stop
 * overlapping altogether, and nothing here goes that far: even the furthest
 * apart result keeps a sliver of shared ground, because there always is some.
 */
function circleSeparation(
  category: ResultCategory,
  score: number,
  hasConflict: boolean,
) {
  if (category === 'different-paths') {
    // A gate is not a near miss. It sits at the far end regardless of score.
    if (hasConflict) return 108;
    return lerp(104, 92, within(score, 0, CATEGORY_THRESHOLDS.worthAConversation));
  }
  return lerp(
    86,
    58,
    within(
      score,
      CATEGORY_THRESHOLDS.worthAConversation,
      CATEGORY_THRESHOLDS.strongPotential,
    ),
  );
}

function Circles({
  category,
  score,
  hasConflict,
  clipId,
}: AlignmentDiagramProps & { clipId: string }) {
  const separation = circleSeparation(category, score, hasConflict ?? false);
  const cy = 92;
  const left = VIEW.midX - separation / 2;
  const right = VIEW.midX + separation / 2;

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <circle cx={left} cy={cy} r={CIRCLE_R} />
        </clipPath>
      </defs>

      {/* The animation lives on a wrapper rather than the shape itself: a CSS
          transform on an SVG element replaces its `transform` attribute
          outright, which would quietly undo the rotation on the ovals below. */}
      <g className={styles.shapeLeft}>
        <circle
          cx={left}
          cy={cy}
          r={CIRCLE_R}
          fill="color-mix(in oklab, var(--voice-james) 15%, transparent)"
          stroke="var(--voice-james)"
          strokeOpacity="0.7"
          strokeWidth="1.6"
        />
      </g>
      <g className={styles.shapeRight}>
        <circle
          cx={right}
          cy={cy}
          r={CIRCLE_R}
          fill="color-mix(in oklab, var(--voice-you) 13%, transparent)"
          stroke="var(--voice-you)"
          strokeWidth="1.6"
        />
      </g>

      {/* The shared area, drawn as one circle clipped by the other. */}
      <g clipPath={`url(#${clipId})`} className={styles.shared}>
        <circle
          cx={right}
          cy={cy}
          r={CIRCLE_R}
          fill="color-mix(in oklab, var(--align-partial) 22%, transparent)"
        />
      </g>
    </>
  );
}

/* ---------------------------------------------------------------------------
   Two ovals, meeting in a heart — the strongest ending only
   ---------------------------------------------------------------------------
   The same two-fields-with-an-overlap idea as the circles, drawn as ovals that
   taper to a point instead. Both points meet at the bottom of the frame, so
   the pair of them reads as a heart: two round lobes at the top, two straight
   flanks sweeping down to a tip.

   Nothing about it is faked. The two shapes are real, the overlap between them
   is a real intersection of those shapes, and the heart is what the pair
   happens to make when two people's answers land this close together. Move
   them apart and it stops being one.

   The heart is used once in this whole product, here. If it turns up on a
   second screen it stops meaning anything.
   --------------------------------------------------------------------------- */

const HEART = {
  /** The round end of each oval — the lobes of the heart. */
  lobeRadius: 52,
  lobeCy: 80,
  /** Where the two points meet. */
  tipX: VIEW.midX,
  tipY: 170,
} as const;

/**
 * How far each lobe sits from the middle. This is the overlap: bring the two
 * lobes together and the shared area swells and the cleft between them closes,
 * pull them apart and the heart widens and the cleft cuts deeper.
 *
 * Never past the radius, because at that point the lobes separate entirely and
 * the shape falls into two leaves.
 */
function heartSpread(score: number) {
  const t = within(score, CATEGORY_THRESHOLDS.strongPotential, 1);
  return HEART.lobeRadius * lerp(0.74, 0.42, t);
}

/**
 * One oval: a circle for the lobe, and the two tangents from it down to the
 * shared point. Tangents rather than a curve because they are what give the
 * heart its straight lower flanks — an ellipse bulges there and the shape
 * reads as a pair of petals instead.
 */
function ovalPath(cx: number, cy: number, r: number, tipX: number, tipY: number) {
  const distance = Math.hypot(tipX - cx, tipY - cy);
  const toTip = Math.atan2(tipY - cy, tipX - cx);
  const half = Math.acos(r / distance);

  const touch = (angle: number) =>
    `${(cx + r * Math.cos(angle)).toFixed(2)} ${(cy + r * Math.sin(angle)).toFixed(2)}`;

  // Out to one tangent point, the long way round the lobe, back to the point.
  return `M ${tipX} ${tipY} L ${touch(toTip + half)} A ${r} ${r} 0 1 1 ${touch(
    toTip - half,
  )} Z`;
}

function Heart({ score, clipId }: { score: number; clipId: string }) {
  const spread = heartSpread(score);
  const { lobeRadius, lobeCy, tipX, tipY } = HEART;

  const left = ovalPath(tipX - spread, lobeCy, lobeRadius, tipX, tipY);
  const right = ovalPath(tipX + spread, lobeCy, lobeRadius, tipX, tipY);

  return (
    <>
      <defs>
        <clipPath id={clipId}>
          <path d={left} />
        </clipPath>
      </defs>

      <g className={styles.shapeLeft}>
        <path
          d={left}
          fill="color-mix(in oklab, var(--voice-james) 15%, transparent)"
          stroke="var(--voice-james)"
          strokeOpacity="0.7"
          strokeWidth="1.6"
        />
      </g>
      <g className={styles.shapeRight}>
        <path
          d={right}
          fill="color-mix(in oklab, var(--voice-you) 13%, transparent)"
          stroke="var(--voice-you)"
          strokeWidth="1.6"
        />
      </g>

      {/* The shared middle. The one place raspberry is allowed. */}
      <g clipPath={`url(#${clipId})`} className={styles.shared}>
        <path d={right} fill="color-mix(in oklab, var(--c-raspberry-500) 28%, transparent)" />
      </g>
    </>
  );
}

/* ---------------------------------------------------------------------------
   The component itself
   --------------------------------------------------------------------------- */

export function AlignmentDiagram({
  category,
  score,
  hasConflict = false,
}: AlignmentDiagramProps) {
  // Two diagrams can share a page in tests and in Storybook-ish previews, and
  // duplicate clipPath ids would silently clip the wrong one.
  const clipId = `awc-shared-${useId()}`;
  const isHeart = category === 'strong-potential';

  return (
    <div className={`${styles.wrap} ${isHeart ? styles.halo : ''}`}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        fill="none"
        role="img"
        aria-label={LABEL[category]}
      >
        {isHeart ? (
          <Heart score={score} clipId={clipId} />
        ) : (
          <Circles
            category={category}
            score={score}
            hasConflict={hasConflict}
            clipId={clipId}
          />
        )}
      </svg>
    </div>
  );
}
